import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Save,
  Download,
  RefreshCw,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Upload,
  AlertCircle,
  Calendar,
  HardDrive,
  FileJson,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  getConfigBackups,
  createAndUploadBackup,
  restoreBackup,
  deleteBackup,
  ConfigBackup,
} from '../../services/backupService';
import { invalidateTenantCache } from '../../services/tenantCache';

interface TenantConfigBackupProps {
  tenantId: number;
  domainId: number;
  domainName: string;
  currentConfig: any;
}

export function TenantConfigBackup({
  tenantId,
  domainId,
  domainName,
  currentConfig,
}: TenantConfigBackupProps) {
  const [backups, setBackups] = useState<ConfigBackup[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBackups, setTotalBackups] = useState(0);
  const pageSize = 20;

  // Filtres
  const [backupTypeFilter, setBackupTypeFilter] = useState<'all' | 'manual' | 'automatic'>('all');

  // Charger les sauvegardes
  const loadBackups = async () => {
    setLoading(true);
    try {
      const response = await getConfigBackups(
        tenantId,
        currentPage,
        pageSize,
        backupTypeFilter === 'all' ? undefined : backupTypeFilter
      );

      setBackups(response.backups);
      setTotalPages(response.totalPages);
      setTotalBackups(response.total);
    } catch (error) {
      console.error('Erreur lors du chargement des sauvegardes:', error);
      toast.error('Impossible de charger les sauvegardes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, [currentPage, backupTypeFilter, tenantId]);

  // Créer une sauvegarde manuelle
  const handleCreateBackup = async () => {
    if (!currentConfig) {
      toast.error('Aucune configuration à sauvegarder');
      return;
    }

    setCreating(true);
    try {
      const result = await createAndUploadBackup(
        tenantId,
        domainId,
        domainName,
        currentConfig
      );

      if (result.success) {
        toast.success('Sauvegarde créée et uploadée vers Google Drive avec succès');
        loadBackups();
      } else {
        toast.error(result.error || 'Échec de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur lors de la création de la sauvegarde:', error);
      toast.error('Erreur lors de la création de la sauvegarde');
    } finally {
      setCreating(false);
    }
  };

  // Restaurer une sauvegarde
  const handleRestoreBackup = async (backupId: string, backupDomainName: string) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir restaurer la configuration depuis cette sauvegarde ?\n\nDomaine: ${backupDomainName}\n\nLa configuration actuelle sera remplacée.`
      )
    ) {
      return;
    }

    setRestoring(backupId);
    try {
      const result = await restoreBackup(backupId);

      if (result.success) {
        toast.success(
          `Configuration restaurée avec succès pour le domaine ${result.domainName}`
        );
        
        // Invalider le cache tenant
        invalidateTenantCache(tenantId.toString());
        
        // Recharger la page après 1 seconde
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error(result.error || 'Échec de la restauration');
      }
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      toast.error('Erreur lors de la restauration');
    } finally {
      setRestoring(null);
    }
  };

  // Supprimer une sauvegarde
  const handleDeleteBackup = async (backupId: string, backupDomainName: string) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer cette sauvegarde ?\n\nDomaine: ${backupDomainName}\n\nCette action est irréversible.`
      )
    ) {
      return;
    }

    setDeleting(backupId);
    try {
      await deleteBackup(backupId);
      toast.success('Sauvegarde supprimée avec succès');
      loadBackups();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  // Formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Icône de statut
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'uploading':
        return <Upload className="h-5 w-5 text-blue-400 animate-pulse" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  // Badge de statut
  const getStatusBadge = (status: string) => {
    const baseClass = 'px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return <span className={`${baseClass} bg-green-500/20 text-green-400`}>Complété</span>;
      case 'failed':
        return <span className={`${baseClass} bg-red-500/20 text-red-400`}>Échec</span>;
      case 'uploading':
        return <span className={`${baseClass} bg-blue-500/20 text-blue-400`}>En cours</span>;
      case 'pending':
        return <span className={`${baseClass} bg-yellow-500/20 text-yellow-400`}>En attente</span>;
      default:
        return <span className={`${baseClass} bg-gray-500/20 text-gray-400`}>Inconnu</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white flex items-center">
            <HardDrive className="h-5 w-5 mr-2" />
            Sauvegardes de configuration
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            Gérez les sauvegardes automatiques et manuelles de votre configuration (Google Drive)
          </p>
        </div>
        <button
          onClick={handleCreateBackup}
          disabled={creating}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:from-green-700 hover:to-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {creating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Création...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Créer une sauvegarde
            </>
          )}
        </button>
      </div>

      {/* Informations */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">Sauvegardes automatiques</p>
            <p className="text-blue-300/80">
              Les sauvegardes automatiques sont effectuées <strong>2 fois par jour</strong> (à 06h00
              et 18h00 UTC) et stockées dans Google Drive. Vous pouvez également créer des sauvegardes
              manuelles à tout moment.
            </p>
          </div>
        </div>
      </div>

      {/* Filtres et statistiques */}
      <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-300">Type:</label>
            <select
              value={backupTypeFilter}
              onChange={(e) => {
                setBackupTypeFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Tous</option>
              <option value="manual">Manuelles</option>
              <option value="automatic">Automatiques</option>
            </select>
          </div>
          <button
            onClick={loadBackups}
            disabled={loading}
            className="flex items-center px-3 py-1.5 text-sm text-gray-300 bg-white/5 border border-white/10 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
        <div className="text-sm text-gray-400">
          Total: <span className="text-white font-medium">{totalBackups}</span> sauvegarde
          {totalBackups > 1 ? 's' : ''}
        </div>
      </div>

      {/* Liste des sauvegardes */}
      <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
        {loading && backups.length === 0 ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : backups.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <FileJson className="h-12 w-12 text-gray-500 mb-3" />
            <p className="text-gray-400 text-sm">Aucune sauvegarde disponible</p>
            <p className="text-gray-500 text-xs mt-1">
              Créez votre première sauvegarde pour commencer
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/20 border-b border-white/10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Domaine
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Google Drive
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {backups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(backup.backup_status)}
                        {getStatusBadge(backup.backup_status)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-sm text-white font-mono">
                          {backup.domain_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          backup.backup_type === 'manual'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-cyan-500/20 text-cyan-400'
                        }`}
                      >
                        {backup.backup_type === 'manual' ? 'Manuelle' : 'Automatique'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm text-gray-300">
                        <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                        {formatDate(backup.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400">
                        {formatFileSize(backup.file_size)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {backup.google_drive_file_url ? (
                        <a
                          href={backup.google_drive_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-blue-400 hover:text-blue-300 hover:underline"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Ouvrir
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleRestoreBackup(backup.id, backup.domain_name)}
                          disabled={
                            restoring === backup.id || backup.backup_status !== 'completed'
                          }
                          className="p-2 text-green-400 hover:bg-green-500/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Restaurer cette sauvegarde"
                        >
                          {restoring === backup.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteBackup(backup.id, backup.domain_name)}
                          disabled={deleting === backup.id}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Supprimer cette sauvegarde"
                        >
                          {deleting === backup.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="text-sm text-gray-400">
            Page {currentPage} sur {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-1.5 text-sm text-gray-300 bg-white/5 border border-white/10 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-1.5 text-sm text-gray-300 bg-white/5 border border-white/10 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
